/**
 * THE DISCLAIMER BUILDS ITSELF FROM THE REQUEST (2026-10-02, milestone 2 enhancement).
 *
 * Every eblast starts from its template's DEFAULT disclaimer — the baselines Digital Ops
 * supplied in "Default disclaimers.docx" (filed with the spec as
 * ech/spec/DEFAULT-DISCLAIMERS-BY-TEMPLATE-20261002.docx in the prototype folder). Anything
 * else the email mentions (schools, garages, a ranking claim…) is added before the
 * closing copyright line, using the Disclaimer Builder's own wording.
 *
 * Item NAMES are the real records behind the Disclaimer Builder Request Form
 * (Legal Reviews base, form pag6B6mkW7oCy3fN7, "Relevant in Most Ads" and
 * "Special or Unique Mentions"), so a later step can file the request with them as-is.
 *
 * Out of scope here, on purpose: offers (milestone 3/4), writing to Airtable, and
 * filing the Legal Reviews record. Pure functions only — no React, no network.
 */

import RULES from "./disclaimer-rules.json";
import { legalCopy } from "./disclaimerLegal";

export type ItemList = "rel" | "spec";
export interface DisclaimerItem { key: string; list: ItemList; name: string; copy: string | null; legalId?: string }

/*
 * THE RULES ARE DATA (2026-10-07, Jack: "a spreadsheet fully mapped … so the rules get auto
 * pulled in reference to the spreadsheet"). Every item, its footer copy, the words that tag it
 * and the words that must NOT tag it live in disclaimer-rules.json, which round-trips with the
 * team's DISCLAIMER-RULES.xlsx (scripts/disclaimer-rules.py). Change a rule there, not here.
 */
interface RuleRow { key: string; legalId?: string; list: ItemList; name: string; copy: string | null; triggers: string[]; excludes: string[]; mark: string; followUp: string; notes: string }
const ROWS = (RULES as unknown as { items: RuleRow[]; food: string[] }).items;
const FOOD_WORDS = (RULES as unknown as { items: RuleRow[]; food: string[] }).food;

/** Disclaimer Builder items, in chip and footer order. `copy: null` = wording comes from answers. */
export const DISCLAIMER_ITEMS: DisclaimerItem[] = ROWS.map((r) => ({ key: r.key, list: r.list, name: r.name, copy: r.copy, legalId: r.legalId }));
export const ITEM = Object.fromEntries(DISCLAIMER_ITEMS.map((i) => [i.key, i])) as Record<string, DisclaimerItem>;

const FEAT = ITEM.features.copy as string;
const SQFT = ITEM.sqft.copy as string;
const PRICES = ITEM.prices.copy as string;
export const FOOD_LINE = "Food quantities limited and available while supplies last.";
/**
 * The footer line for an item: legal's Disclaimer Copy once Legal Reviews › Disclaimer Options
 * has been read (disclaimerLegal.ts), else the bundled copy. Legal copy that still has a fill-in
 * bracket is skipped, so the app's own built line stays (2026-10-28).
 */
export function copyFor(key: string): string | null {
  const it = ITEM[key];
  if (!it) return null;
  return legalCopy(it.legalId) || it.copy;
}
export const INTEREST_NOTE = "¹ There is no guarantee that participation in the Interest List Program will result in priority over other buyers who do not comply with the foregoing requirements. Joining the Interest List Program does not guarantee ability to for buyer to purchase a home and is not a commitment for a purchase opportunity for an available home. Lennar reserves the right to change or withdraw the Interest List Program without notice.";

export interface Baseline { doc: string; note?: string; body: string[]; marks: string[]; covers: string[] }
/** "@FOOD" marks where the food line goes — only once Event Food = Yes. */
const BASELINES: Record<string, Baseline> = {
  "Coming Soon": { doc: "Coming Soon", note: INTEREST_NOTE, body: [FEAT, PRICES], marks: ["Lennar", "the Lennar logo"], covers: ["features", "prices", "interest"] },
  "Join the VIP List": { doc: "Coming Soon", note: INTEREST_NOTE, body: [FEAT, PRICES], marks: ["Lennar", "the Lennar logo"], covers: ["features", "prices", "interest"] },
  "Available Now": { doc: "Available Now", body: [FEAT, SQFT, PRICES], marks: ["Lennar", "the Lennar logo"], covers: ["features", "sqft", "prices"] },
  "Actively Selling": { doc: "Actively Selling", body: [FEAT, SQFT, PRICES], marks: ["Lennar", "the Lennar logo", "Next Gen"], covers: ["features", "sqft", "prices"] },
  "Just Launched": { doc: "Just Launched", body: [FEAT, SQFT, PRICES], marks: ["Lennar", "the Lennar logo", "Next Gen"], covers: ["features", "sqft", "prices"] },
  "Grand Opening": { doc: "Grand Opening Invite", body: ["Event is subject to change without notice.", "@FOOD", "Contact a New Home Consultant for additional information.", FEAT], marks: ["Lennar", "the Lennar logo"], covers: ["features", "events"] },
};
/** No default in the doc (e.g. Grand Opening Thank You): Features + the footer only. */
const GENERIC: Baseline = { doc: "", body: [FEAT], marks: ["Lennar", "the Lennar logo"], covers: ["features"] };

/**
 * Template name → baseline. Matched on the form's own template name, NOT `echTplFor`:
 * that resolver folds "Sustain - Actively Selling" into "Available Now", and the two
 * defaults differ (Actively Selling carries Next Gen). Order matters: "Grand Opening
 * Thank You" is not an invite, and "Join the VIP List" is checked before "Coming Soon".
 */
export function baselineKey(template: string): string | null {
  const t = String(template || "").toLowerCase();
  if (/grand opening/.test(t)) return /thank/.test(t) ? null : "Grand Opening";
  if (/vip/.test(t)) return "Join the VIP List";
  if (/coming soon/.test(t)) return "Coming Soon";
  if (/actively selling/.test(t)) return "Actively Selling";
  if (/just launched/.test(t)) return "Just Launched";
  if (/available now/.test(t)) return "Available Now";
  /* the live library's Sustain template is "Find Your Dream Home - New Homes Now Selling";
     the MVP list calls the same stage "Sustain - Actively Selling" (assumed match — confirm
     with Digital Ops). Checked after "just launched": "Now Selling - Just Launched" exists too. */
  if (/find your dream home|now selling/.test(t)) return "Actively Selling";
  return null;
}
export function baselineFor(template: string): Baseline {
  const k = baselineKey(template);
  return k ? BASELINES[k] : GENERIC;
}

/*
 * PHRASE SYNTAX (what the spreadsheet holds): case-insensitive whole words · {n} = any number
 * ("300", "2,400", "300s", "300k", "300ks") · (s) = optional plural · * = the rest of the word
 * ("award*" → award, awards, award-winning). Curly quotes count as straight ones.
 */
const NUM = "\\d[\\d,]*(?:\\.\\d+)?(?:[kKM]s?|s)?";
const ALNUM = "a-z0-9";
function phraseSource(p: string): string {
  const t = p.trim().toLowerCase().replace(/[’‘]/g, "'");
  const body = t.split(/(\{n\}|\(s\)|\*)/).map((part) => {
    if (part === "{n}") return NUM;
    if (part === "(s)") return "s?";
    if (part === "*") return "[\\w'-]*";
    return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  }).join("");
  const head = /^([a-z0-9]|\{n\})/.test(t) ? "(?<![" + ALNUM + "])" : "";
  const tail = /([a-z0-9]|\{n\}|\(s\)|\*)$/.test(t) ? "(?![" + ALNUM + "])" : "";
  return head + body + tail;
}
/** one regex for a phrase list; longest phrase first, so the earliest match is also the fullest */
export function compilePhrases(list: string[]): RegExp | null {
  const ps = (list || []).map((p) => p.trim()).filter(Boolean).sort((x, y) => y.length - x.length);
  return ps.length ? new RegExp(ps.map(phraseSource).join("|"), "i") : null;
}
const norm = (t: string) => t.replace(/[’‘]/g, "'").replace(/[“”]/g, "\"");
/** the first trigger in `text` (exclusions blanked out first), as written in the copy, or "" */
export function findPhrase(text: string, re: RegExp | null, ex: RegExp | null): string {
  if (!re || !text) return "";
  let n = norm(text);
  if (ex) n = n.replace(new RegExp(ex.source, "gi"), (m) => " ".repeat(m.length));
  const m = re.exec(n);
  return m ? text.slice(m.index, m.index + m[0].length) : "";
}
export const COPY_RULES = ROWS.map((r) => ({ key: r.key, re: compilePhrases(r.triggers), ex: compilePhrases(r.excludes) })).filter((r) => r.re);
const FOOD_RE = compilePhrases(FOOD_WORDS);
const LM_RE = compilePhrases(["lennar mortgage"]);

/** one piece of requester copy and the field it sits in ("Subject line", "Hero · Headline") */
export interface CopyPiece { where: string; text: string }
export interface DetectInput {
  template: string;
  reqFocus?: string;
  /** every piece of copy the requester controls: subject, preheader, block text.
   *  A plain string still works; a `{ where, text }` lets the reason name its field. */
  copy: Array<string | CopyPiece>;
  /** set when the email carries an amenity-icon block with icons picked —
   *  the field the reason names, e.g. "Amenities · Pool, Clubhouse" (2026-10-07) */
  amenities?: string;
}
export interface Picked { key: string; why: string; fromTemplate: boolean }
/** Acreage / Dimensions / Square Footage — the Disclaimer Builder form's own follow-up
 *  ("What is mentioned in your creative?") whenever Square Footage is picked */
export const SQFT_TYPES = ["Square Footage", "Acreage", "Dimensions", "Square Footage, Dimensions & Acreage"] as const;
export interface Detected {
  baseline: Baseline;
  items: Picked[];
  marks: { name: string; why: string }[];
  vip: { name: string; why: string } | null;
  /** what the copy says, when it decides the sq-ft follow-up for the requester */
  sqftHint: string;
  /** set when the copy says the event serves food ("“food” in Body Copy") — pre-answers Event Food */
  foodHint: string;
}

/**
 * Template default + everything else the request mentions. Each picked item carries a
 * `why` that names WHERE it came from — the template, a Step 1 field, or the exact field
 * of the email that said it (Disclaimer Artifact Review, 2026-10-07: "hover over it and
 * say, this is the field it's coming from"). Keyword rules only — no AI.
 */
export function detect(inp: DetectInput): Detected {
  const tplLabel = baselineKey(inp.template) || inp.template || "Template";
  const baseline = baselineFor(inp.template);
  const seen = new Map<string, Picked>();
  const put = (key: string, why: string, fromTemplate = false) => { if (!seen.has(key)) seen.set(key, { key, why, fromTemplate }); };
  baseline.covers.forEach((k) => put(k, "Part of the " + tplLabel + " default disclaimer", true));
  if ((inp.reqFocus || "").toLowerCase() === "community") put("community", "Step 1 · Email Request Focus is Community");
  if (inp.amenities) put("community", "Amenity icons picked in " + inp.amenities);
  const pieces: CopyPiece[] = inp.copy
    .map((c) => (typeof c === "string" ? { where: "", text: c } : c))
    /* never read the legal text itself: the footer's "Legal / disclaimer" field and the
       ECH_Disclaimer block hold the placeholder disclaimer, which says "amenities", "prices"…
       (Chrome sweep 2026-10-07: every template picked up Community from the Social Footer) */
    .filter((c) => c && c.text && !/legal|disclaimer/i.test(c.where));
  COPY_RULES.forEach(({ key, re, ex }) => {
    for (const p of pieces) {
      const hit = findPhrase(p.text, re, ex);
      if (hit) { put(key, "“" + hit.trim() + "” in " + (p.where || "your email")); break; }
    }
  });
  const items = DISCLAIMER_ITEMS.filter((i) => seen.has(i.key)).map((i) => seen.get(i.key) as Picked);
  const marks: { name: string; why: string }[] = ROWS.filter((r) => r.mark && seen.has(r.key))
    .map((r) => ({ name: r.mark, why: seen.get(r.key)!.why }));
  /* "Lennar Mortgage" in the copy still adds the Lennar Mortgage mark, but no longer tags
     Get Pre-Qualified: it matched every financing offer (571 flagged, 13 right in 1,100 sent
     eblasts — audit 2026-10-27) */
  if (!marks.some((m) => m.name === "Lennar Mortgage")) {
    for (const p of pieces) { const hit = findPhrase(p.text, LM_RE, null); if (hit) { marks.push({ name: "Lennar Mortgage", why: "“" + hit.trim() + "” in " + (p.where || "your email") }); break; } }
  }
  const k = baselineKey(inp.template);
  const vipName = seen.has("prequal") ? "VIP List + Prequal" : "VIP";
  const vip = k === "Join the VIP List" || k === "Coming Soon"
    ? { name: vipName, why: "The " + k + " template" }
    : seen.has("interest") ? { name: vipName, why: seen.get("interest")!.why } : null;
  const all = pieces.map((p) => p.text).join("\n");
  let foodHint = "";
  for (const p of pieces) { const hit = findPhrase(p.text, FOOD_RE, null); if (hit) { foodHint = "“" + hit.trim() + "” in " + (p.where || "your email"); break; } }
  const hasArea = /sq\.?\s?ft|square f(ee|oo)t/i.test(all), hasAcre = /\bacres?\b|acreage/i.test(all);
  const sqftHint = hasArea && hasAcre ? SQFT_TYPES[3] : hasAcre ? "Acreage" : hasArea ? "Square Footage" : "";
  return { baseline, items, marks, vip, sqftHint, foodHint };
}

/** the sq-ft line for the chosen follow-up answer */
export function sqftCopy(type: string): string {
  switch (type) {
    case "Acreage": return "Acreage is estimated; actual acreage will differ.";
    case "Dimensions": return "Dimensions are estimated; actual dimensions will differ.";
    case "Square Footage, Dimensions & Acreage": return "Sq. ft., dimensions and acreage are estimated; actual sq. ft., dimensions and acreage will differ.";
    default: return ITEM.sqft.copy as string;
  }
}

export interface Answers {
  eventFood?: string;          // "Yes" | "No" | ""
  accoladeType?: string;       // "Awards" | "Rankings" | ""
  accoladeOrg?: string;
  accoladeYear?: string;
  /** Acreage / Dimensions / Square Footage — empty means "use what the copy says" */
  sqftType?: string;
  /** keys the requester added / removed by hand (template items cannot be removed) */
  added?: string[];
  removed?: string[];
  /** an existing Disclaimer Builder request number, already cleaned by disclaimerId() */
  requestNo?: string;
}
export interface Ctx {
  division: string; deploy?: string;
  /** the Seller's Broker / Construction License line (disclaimerLicense.ts). `""` = none
   *  required; `null` or left out = unknown, so the two placeholders stay. */
  license?: string | null;
}

/** the items that end up on the request: detected − removed (template ones stay) + added */
export function selected(det: Detected, a: Answers): Picked[] {
  const removed = new Set(a.removed || []);
  const out = det.items.filter((p) => p.fromTemplate || !removed.has(p.key));
  (a.added || []).forEach((k) => { if (ITEM[k] && !out.some((p) => p.key === k)) out.push({ key: k, why: "Added by you", fromTemplate: false }); });
  const order = DISCLAIMER_ITEMS.map((i) => i.key);
  return out.sort((x, y) => order.indexOf(x.key) - order.indexOf(y.key));
}

/** which follow-up questions the Disclaimer Builder form would ask */
export function followUps(det: Detected, a: Answers): { eventFood: boolean; accolades: boolean; sqft: boolean } {
  const keys = new Set(selected(det, a).map((p) => p.key));
  /* Event Food is asked whenever Events is on the request (the Grand Opening template, or
     an invitation in the copy). "Will this be a Grand Opening?" is never asked: the
     template answers it.
     Sq-ft: the form asks "Acreage, Dimensions or Square Footage?" whenever Square Footage
     is picked; a template that already covers it (Available Now…) has answered it. */
  return {
    eventFood: keys.has("events"),
    accolades: keys.has("awards"),
    sqft: keys.has("sqft") && det.baseline.covers.indexOf("sqft") < 0,
  };
}

/** the Event Food answer in force: the requester's pick, else Yes when the copy offers food */
export function eventFoodAnswer(det: Detected, a: Answers): string { return a.eventFood || (det.foodHint ? "Yes" : ""); }

/** the sq-ft answer in force: the requester's pick, else what the copy says */
export function sqftAnswer(det: Detected, a: Answers): string { return a.sqftType || det.sqftHint || ""; }

/** what's still missing before the request is complete */
export function missing(det: Detected, a: Answers): string[] {
  const f = followUps(det, a), out: string[] = [];
  if (f.eventFood && !eventFoodAnswer(det, a)) out.push("Event Food");
  if (f.sqft && !sqftAnswer(det, a)) out.push("Acreage, Dimensions or Square Footage");
  if (f.accolades && !a.accoladeType) out.push("Accolade Type");
  if (f.accolades && !(a.accoladeOrg || "").trim()) out.push("Accolades Organization Name");
  return out;
}

function joinMarks(m: string[]): string { return m.length < 3 ? m.join(" and ") : m.slice(0, -1).join(", ") + " and " + m[m.length - 1]; }
function mmyy(deploy?: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(deploy || "");
  const d = m ? { y: m[1], mo: m[2] } : { y: String(new Date().getFullYear()), mo: String(new Date().getMonth() + 1).padStart(2, "0") };
  return d.mo + "/" + d.y.slice(2);
}
function year(deploy?: string): string { const m = /^(\d{4})/.exec(deploy || ""); return m ? m[1] : String(new Date().getFullYear()); }

/**
 * The finished disclaimer as paragraphs: [request-# line] · [template footnote] · one body
 * paragraph = template default + added items + the footer with the template's marks.
 */
export function assemble(det: Detected, a: Answers, ctx: Ctx): string[] {
  const covered = new Set(det.baseline.covers);
  const paras: string[] = [];
  if (a.requestNo) paras.push("[Final Disclaimer · Disclaimer Builder #" + a.requestNo + "]");
  if (det.baseline.note) paras.push(det.baseline.note);
  const keys = new Set(selected(det, a).map((p) => p.key));
  /* an Interest List / VIP List CTA carries ¹ — its footnote ships even when the template's
     default doesn't include it (Just Launched's "Join VIP List¹", Chrome sweep 2026-10-07) */
  if (keys.has("interest") && !covered.has("interest")) paras.push(INTEREST_NOTE);
  const food = eventFoodAnswer(det, a) === "Yes";
  /* the template default reads legal's wording for Features / Prices once it has loaded */
  const body = det.baseline.body.flatMap((t) => (t === "@FOOD" ? (food ? [FOOD_LINE] : [])
    : t === FEAT ? [copyFor("features") as string] : t === PRICES ? [copyFor("prices") as string] : [t]));
  selected(det, a).filter((p) => !covered.has(p.key)).forEach((p) => {
    if (p.key === "awards") {
      const org = (a.accoladeOrg || "").trim(), yr = (a.accoladeYear || "").trim();
      body.push(org ? (a.accoladeType === "Rankings" ? "Based on " : "Awarded by ") + org + (yr ? " (" + yr + ")" : "") + "." : "[Awarded by/Based on] __________.");
    } else if (p.key === "sqft") {
      body.push(sqftCopy(sqftAnswer(det, a)));
    } else if (p.key === "events") {
      body.push(ITEM.events.copy as string);
      if (food) body.push(FOOD_LINE);
    } else if (copyFor(p.key)) body.push(copyFor(p.key) as string);
  });
  const marks = det.baseline.marks.concat(det.marks.map((m) => m.name).filter((m) => det.baseline.marks.indexOf(m) < 0));
  const div = (ctx.division || "").trim() || "Division";
  const lic = typeof ctx.license === "string" ? ctx.license.trim() : null;
  const licPart = lic === null ? "[" + div + " Seller’s Broker] [" + div + " Construction Licenses] " : lic ? lic + " " : "";
  body.push("This is not an offer in states where prior registration is required. Void where prohibited by law. Copyright © " + year(ctx.deploy) + " Lennar Corporation. " + joinMarks(marks) + " are U.S. registered service marks or service marks of Lennar Corporation and/or its subsidiaries. " + licPart + "Date " + mmyy(ctx.deploy));
  paras.push(body.join(" "));
  return paras;
}

/**
 * WHICH DISCLAIMER WAS USED, AS ONE LINE (2026-10-14, Katy's "Disclaimer Builder Detail").
 * Katy: "For 'which disclaimer was used', what exactly would you record?" — the template's
 * default, what was added on top of it, what the requester took off, the answers that
 * changed the wording, the footer block, and the rules version. Item names are the Legal
 * Reviews form's own (Relevant in Most Ads / Special or Unique Mentions), so the line can be
 * matched to that form as-is. The request # is NOT repeated here: it has its own column.
 *
 *   Default: Coming Soon · Added: Schools, Prices · Removed: Garages · Event Food: Yes ·
 *   Footer: ECH_DisclaimerMortgage · Rules: 2026-10-07
 */
export const RULES_VERSION = String((RULES as unknown as { version?: string }).version || "");
export function disclaimerDetail(template: string, det: Detected, a: Answers, footerBlock?: string): string {
  const covered = new Set(det.baseline.covers);
  const name = (k: string) => (ITEM[k] ? ITEM[k].name : k);
  const added = selected(det, a).filter((p) => !covered.has(p.key)).map((p) => name(p.key));
  const removed = (a.removed || []).filter((k) => det.items.some((p) => p.key === k && !p.fromTemplate)).map(name);
  const parts: string[] = ["Default: " + (baselineKey(template) || "Generic (no template default)")];
  parts.push("Added: " + (added.length ? added.join(", ") : "none"));
  if (removed.length) parts.push("Removed: " + removed.join(", "));
  const keys = new Set(selected(det, a).map((p) => p.key));
  if (keys.has("events")) { const f = eventFoodAnswer(det, a); if (f) parts.push("Event Food: " + f); }
  if (keys.has("sqft") && !covered.has("sqft")) { const t = sqftAnswer(det, a); if (t) parts.push("Sq ft: " + t); }
  if (keys.has("awards") && a.accoladeType) parts.push("Accolade: " + [a.accoladeType, (a.accoladeOrg || "").trim(), (a.accoladeYear || "").trim()].filter(Boolean).join(", "));
  if (footerBlock) parts.push("Footer: " + footerBlock);
  if (RULES_VERSION) parts.push("Rules: " + RULES_VERSION);
  return parts.join(" · ");
}

const escHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * Put the paragraphs into the email's disclaimer block. Targets the first <p> inside the
 * LAST `<!--BEGIN Com disclaimer-->…<!--END Com disclaimer-->` pair (ECH_Disclaimer and its
 * Mortgage variants), keeps that <p>'s own inline style for every paragraph, and leaves the
 * Equal Housing image alone. No block, no change.
 *
 * LAST, not first (found driving the local build, 2026-10-02): ECH_ContactUsDefault carries
 * the same "Com disclaimer" comment around its "We're Here to Help You" copy, and the first
 * version overwrote that. The footer is always placed last (echPlacement: ECH_Disclaimer → "last").
 */
export function applyDisclaimer(html: string, paras: string[]): string {
  if (!html || !paras.length) return html;
  const begin = html.lastIndexOf("<!--BEGIN Com disclaimer-->");
  if (begin < 0) return html;
  const end = html.indexOf("<!--END Com disclaimer-->", begin);
  const seg = html.slice(begin, end < 0 ? undefined : end);
  const m = /<p(\s[^>]*)?>[\s\S]*?<\/p>/i.exec(seg);
  if (!m) return html;
  const open = "<p" + (m[1] || "") + ">";
  const out = seg.slice(0, m.index) + paras.map((p) => open + escHtml(p) + "</p>").join("") + seg.slice(m.index + m[0].length);
  return html.slice(0, begin) + out + (end < 0 ? "" : html.slice(end));
}

/**
 * The visible text of an email, minus its disclaimer footer — so a rule can read copy that
 * never passed through a block the app holds (the template's own default copy, a community
 * fill, a listing table, a duplicated job). Without this, a request that never opened Design
 * Email was only scanned on its subject and preheader (Chrome sweep 2026-10-07: Grand Opening
 * Thank You shows prices and sq. ft. in its plan cards and got neither line).
 */
export function emailText(html: string): string {
  if (!html) return "";
  let h = html;
  const b = h.lastIndexOf("<!--BEGIN Com disclaimer-->");
  if (b >= 0) { const e = h.indexOf("<!--END Com disclaimer-->", b); h = h.slice(0, b) + (e < 0 ? "" : h.slice(e)); }
  return h
    .replace(/<head[\s\S]*?<\/head>/gi, " ").replace(/<(style|script)[\s\S]*?<\/\1>/gi, " ").replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ").replace(/&#0?39;|&rsquo;|&lsquo;/g, "’").replace(/&quot;|&ldquo;|&rdquo;/g, "\"").replace(/&[a-z]+;/gi, " ").replace(/&amp;/g, "&")
    .replace(/\s+/g, " ").trim();
}
